import React from "react";

// Base skeleton component with pulse animation and accessibility support
export interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: "none" | "sm" | "md" | "lg" | "full";
  "aria-label"?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = "",
  width,
  height,
  rounded = "md",
  "aria-label": ariaLabel = "Loading content",
}) => {
  const roundedClasses = {
    none: "",
    sm: "rounded-sm",
    md: "rounded",
    lg: "rounded-lg",
    full: "rounded-full",
  };

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      className={`animate-pulse bg-gray-200 ${roundedClasses[rounded]} ${className}`}
      style={{
        width: width || undefined,
        height: height || undefined,
      }}
    >
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

// Baakas Logo Loader with fade in/out animation
export interface BaakasLogoLoaderProps {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  "aria-label"?: string;
}

export const BaakasLogoLoader: React.FC<BaakasLogoLoaderProps> = ({
  size = "md",
  className = "",
  "aria-label": ariaLabel = "Loading",
}) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-20 h-20",
  };

  return (
    <>
      <style>{`
        @keyframes baakasFadeInOut {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 1;
          }
        }
        .baakas-logo-fade {
          animation: baakasFadeInOut 2s ease-in-out infinite;
        }
      `}</style>
      <div
        role="status"
        aria-label={ariaLabel}
        aria-live="polite"
        className={`${sizeClasses[size]} ${className} flex items-center justify-center`}
      >
        <img
          src="/favicon.ico"
          alt="Baakas"
          className="w-full h-full object-contain baakas-logo-fade"
          loading="eager"
          decoding="async"
          onError={(e) => {
            // Try fallback paths if favicon fails
            const target = e.currentTarget as HTMLImageElement;
            if (target.src.includes('favicon.ico')) {
              target.src = "/assets/baakas_logo.svg";
            } else if (target.src.includes('baakas_logo.svg')) {
              target.src = "/img/baakas_logo_transparent.png";
            } else {
              // Last resort: show a simple div with Baakas text
              target.style.display = 'none';
            }
          }}
        />
        <span className="sr-only">{ariaLabel}</span>
      </div>
    </>
  );
};

// Spinner replacement - circular skeleton (kept for backward compatibility, but uses logo loader)
export interface CircularSkeletonProps {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  "aria-label"?: string;
}

export const CircularSkeleton: React.FC<CircularSkeletonProps> = ({
  size = "md",
  className = "",
  "aria-label": ariaLabel = "Loading",
}) => {
  // Use BaakasLogoLoader instead of circular skeleton
  return <BaakasLogoLoader size={size} className={className} aria-label={ariaLabel} />;
};

// Button skeleton
export const ButtonSkeleton: React.FC<{
  width?: string;
  height?: string;
  className?: string;
}> = ({ width = "w-24", height = "h-10", className = "" }) => {
  return (
    <Skeleton className={`${width} ${height} ${className}`} rounded="lg" />
  );
};

// Text skeleton
export const TextSkeleton: React.FC<{
  lines?: number;
  className?: string;
}> = ({ lines = 1, className = "" }) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          height="h-4"
          width={index === lines - 1 ? "w-3/4" : "w-full"}
          rounded="sm"
        />
      ))}
    </div>
  );
};

// Card skeleton
export const CardSkeleton: React.FC<{
  className?: string;
  showImage?: boolean;
  showText?: boolean;
}> = ({ className = "", showImage = true, showText = true }) => {
  return (
    <div
      className={`animate-pulse rounded-2xl border border-gray-100 bg-white p-4 ${className}`}
    >
      {showImage && <Skeleton className="w-full h-40 mb-4" rounded="lg" />}
      {showText && (
        <div className="space-y-2">
          <Skeleton height="h-4" width="w-3/4" rounded="sm" />
          <Skeleton height="h-4" width="w-1/2" rounded="sm" />
        </div>
      )}
    </div>
  );
};

// Page loading skeleton
export interface PageLoadingSkeletonProps {
  className?: string;
  message?: string;
}

export const PageLoadingSkeleton: React.FC<PageLoadingSkeletonProps> = ({
  className = "",
  message = "Loading page content",
}) => {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={message}
      className={`min-h-screen bg-gray-50 flex items-center justify-center ${className}`}
    >
      <div className="text-center space-y-4">
        <BaakasLogoLoader
          size="xl"
          className="mx-auto"
          aria-label="Loading indicator"
        />
        <TextSkeleton lines={2} className="max-w-xs mx-auto" />
      </div>
      <span className="sr-only">{message}</span>
    </div>
  );
};

// Inline loading skeleton (for buttons, small areas)
export const InlineSkeleton: React.FC<{
  size?: "sm" | "md" | "lg";
  className?: string;
}> = ({ size = "md", className = "" }) => {
  const sizeClasses = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  return (
    <div
      className={`${sizeClasses[size]} animate-pulse bg-gray-200 rounded ${className}`}
    />
  );
};

// CardSkeleton is the replacement for SkeletonCard
// Keeping this export for backward compatibility if needed
export const SkeletonCard = CardSkeleton;

// ============================================
// PERSONALIZED SKELETONS - Match Content Structure
// ============================================
// These use centralized base components but match specific content layouts

// Product Card Skeleton - Matches ProductCard structure
export const ProductCardSkeleton: React.FC<{
  className?: string;
  compact?: boolean;
}> = ({ className = "", compact = false }) => {
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}
    >
      {/* Image */}
      <Skeleton
        className={`w-full ${compact ? "h-32" : "h-48"} mb-3`}
        rounded="lg"
      />

      {/* Content */}
      <div className="p-3 space-y-2">
        {/* Title */}
        <Skeleton height="h-4" width="w-3/4" rounded="sm" />

        {/* Price Row */}
        <div className="flex items-center gap-2">
          <Skeleton height="h-5" width="w-20" rounded="sm" />
          <Skeleton height="h-4" width="w-16" rounded="sm" />
        </div>

        {/* Rating (if not compact) */}
        {!compact && (
          <div className="flex items-center gap-1">
            <Skeleton height="h-3" width="w-16" rounded="sm" />
            <Skeleton height="h-3" width="w-12" rounded="sm" />
          </div>
        )}

        {/* Button */}
        <Skeleton height="h-9" width="w-full" rounded="md" className="mt-2" />
      </div>
    </div>
  );
};

// Store Card Skeleton - Matches AllSellersPage store card structure
export const StoreCardSkeleton: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl p-4 md:p-5 flex items-center gap-4 ${className}`}
    >
      {/* Profile Image */}
      <div className="flex-shrink-0">
        <Skeleton height="h-14" width="w-14" rounded="full" />
      </div>

      {/* Seller Info */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Shop Name */}
        <Skeleton height="h-5" width="w-32" rounded="sm" />

        {/* Rating Row */}
        <div className="flex items-center gap-2">
          <Skeleton height="h-4" width="w-16" rounded="sm" />
          <Skeleton height="h-3" width="w-12" rounded="sm" />
        </div>

        {/* Address Row */}
        <div className="flex items-center gap-1.5">
          <Skeleton height="h-3" width="w-3.5" rounded="sm" />
          <Skeleton height="h-3" width="w-24" rounded="sm" />
        </div>
      </div>

      {/* Follow Button */}
      <div className="flex-shrink-0">
        <Skeleton height="h-9" width="w-20" rounded="lg" />
      </div>
    </div>
  );
};

// Store Carousel Skeleton - Matches StoresDirectoryPage carousel store cards
export const StoreCarouselSkeleton: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div
      className={`flex-shrink-0 flex flex-col items-center gap-2 sm:gap-3 bg-white rounded-xl sm:rounded-2xl px-3 sm:px-4 py-3 sm:py-4 border border-gray-200 min-w-[80px] sm:min-w-[100px] ${className}`}
    >
      {/* Store Icon - Circular */}
      <Skeleton height="h-16 sm:h-20" width="w-16 sm:w-20" rounded="full" />

      {/* Store Name - Below Icon */}
      <div className="text-center min-w-0 w-full">
        <Skeleton
          height="h-3 sm:h-4"
          width="w-full"
          rounded="sm"
          className="mx-auto"
        />
      </div>
    </div>
  );
};

// Store Grid Skeleton - Matches StorePage "Other Sellers" grid layout
export const StoreGridSkeleton: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${className}`}
    >
      {/* Seller Image - Square */}
      <Skeleton className="w-full aspect-square" rounded="lg" />

      {/* Seller Info */}
      <div className="p-2 sm:p-3 space-y-1">
        {/* Shop Name */}
        <Skeleton height="h-3 sm:h-4" width="w-3/4" rounded="sm" />

        {/* Location */}
        <Skeleton height="h-3" width="w-1/2" rounded="sm" />
      </div>
    </div>
  );
};

// Order Card Skeleton - Matches order card structure
export const OrderCardSkeleton: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex justify-between items-center">
          <Skeleton height="h-4" width="w-24" rounded="sm" />
          <Skeleton height="h-4" width="w-16" rounded="sm" />
        </div>

        {/* Product Image Row */}
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} height="h-16" width="w-16" rounded="md" />
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t">
          <Skeleton height="h-4" width="w-20" rounded="sm" />
          <Skeleton height="h-8" width="w-24" rounded="md" />
        </div>
      </div>
    </div>
  );
};

// Category Card Skeleton - Matches category card structure
export const CategoryCardSkeleton: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}
    >
      {/* Image */}
      <Skeleton className="w-full h-40" rounded="lg" />

      {/* Content */}
      <div className="p-4 space-y-2">
        <Skeleton height="h-5" width="w-3/4" rounded="sm" />
        <Skeleton height="h-3" width="w-1/2" rounded="sm" />
      </div>
    </div>
  );
};

// Product Detail Loading - Baakas logo with fade animation
export const ProductDetailLoading: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        {/* Baakas Logo with fade in/out animation */}
        <div className="mb-6">
          <BaakasLogoLoader size="xl" className="mx-auto" aria-label="Loading product" />
        </div>

        {/* Loading text */}
        <p className="text-gray-600 font-medium">Loading product...</p>

        {/* Animated dots */}
        <div className="flex items-center justify-center gap-1 mt-2">
          <span
            className="w-1.5 h-1.5 bg-[#da2516] rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          ></span>
          <span
            className="w-1.5 h-1.5 bg-[#da2516] rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          ></span>
          <span
            className="w-1.5 h-1.5 bg-[#da2516] rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          ></span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SECTION-SPECIFIC SKELETONS FOR HOMEPAGE
// ============================================

// Flash Sale Skeleton - Header + 4 product cards in horizontal carousel
export const FlashSaleSkeleton: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div className={`bg-white border-b border-gray-200 ${className}`}>
      <div className="max-w-7xl mx-auto relative">
        {/* Header Section */}
        <div className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-5 pb-3 sm:pb-4 bg-gradient-to-br from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
            {/* Title */}
            <div className="flex items-center gap-2 sm:gap-2.5">
              <Skeleton height="h-6" width="w-6" rounded="md" />
              <Skeleton height="h-6" width="w-32" rounded="sm" />
            </div>

            {/* Countdown Timer */}
            <div className="flex items-center gap-2">
              <Skeleton height="h-4" width="w-16" rounded="sm" />
              <div className="flex items-center gap-1">
                {[...Array(4)].map((_, i) => (
                  <React.Fragment key={i}>
                    <Skeleton height="h-8" width="w-8" rounded="md" />
                    {i < 3 && <Skeleton height="h-4" width="w-2" rounded="full" />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* View All Link */}
            <Skeleton height="h-4" width="w-16" rounded="sm" />
          </div>
        </div>

        {/* Product Carousel */}
        <div className="px-2 sm:px-4 md:px-6 lg:px-8 pb-3 sm:pb-4">
          <div className="flex gap-2 sm:gap-2.5 md:gap-3 overflow-hidden">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[130px] min-[375px]:w-[140px] sm:w-[150px] md:w-[160px] lg:w-[180px] xl:w-[200px]"
              >
                <ProductCardSkeleton compact={true} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Product Grid Skeleton - Responsive grid (2-6 columns)
export const ProductGridSkeleton: React.FC<{
  count?: number;
  className?: string;
  compact?: boolean;
}> = ({ count = 8, className = "", compact = false }) => {
  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-3 md:gap-4 lg:gap-4 xl:gap-4 ${className}`}
    >
      {[...Array(count)].map((_, idx) => (
        <ProductCardSkeleton key={idx} compact={compact} />
      ))}
    </div>
  );
};

// Carousel Skeleton - Maintains aspect ratio for banners
export const CarouselSkeleton: React.FC<{
  className?: string;
  aspectRatio?: string;
}> = ({ className = "", aspectRatio = "aspect-[21/9]" }) => {
  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
      <div className={`relative ${className}`}>
        <div className={`${aspectRatio} w-full bg-gray-100 rounded-[12px] overflow-hidden`}>
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
          </div>
        </div>
        {/* Indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} height="h-2" width="w-2" rounded="full" />
          ))}
        </div>
      </div>
    </>
  );
};
